/** Best-effort extraction from a pasted BibTeX entry — regex-based rather
 * than a full parser, since the fields we need (title/author/year/doi) are
 * consistently `key = {value}` or `key = "value"` pairs regardless of entry
 * type. Whatever it misses, the review step lets the user fill in by hand. */
export function parseBibtex(text) {
  const field = (name) => {
    // \b so "title" doesn't match inside "booktitle" — BibTeX field names
    // are only ever preceded by "{" or "," or whitespace, never a letter.
    const match = text.match(new RegExp(`\\b${name}\\s*=\\s*[{"]([^}"]*)[}"]`, 'i'));
    return match ? match[1].replace(/[{}]/g, '').replace(/\s+/g, ' ').trim() : '';
  };

  const authorRaw = field('author');
  const authors = authorRaw
    ? authorRaw.split(/\s+and\s+/i).map((name) => {
        const parts = name.split(',').map((s) => s.trim());
        return parts.length === 2 ? `${parts[1]} ${parts[0]}` : name.trim();
      })
    : [];

  const doi = field('doi');

  // IEEE-style exports separate keywords with ";"; other sources use ",".
  // Split on whichever shows up rather than assuming one.
  const keywordsRaw = field('keywords');
  const keywords = keywordsRaw
    ? keywordsRaw.split(/[;,]/).map((k) => k.trim()).filter(Boolean)
    : [];

  return {
    title: field('title'),
    authors,
    year: field('year'),
    doi_or_url: doi ? (doi.startsWith('http') ? doi : `https://doi.org/${doi}`) : field('url'),
    keywords,
  };
}

/** Rough, best-effort extraction from a free-text citation (APA/MLA/IEEE/
 * whatever style someone pastes). There's no reliable general parser for
 * arbitrary citation formats without a citation-parsing service, so this
 * only pulls the parts that are unambiguous — a DOI if present, a 4-digit
 * year, and a quoted title if the citation happens to quote one — and
 * leaves everything else for the review step. */
export function parseCitation(text) {
  const clean = text.replace(/\s+/g, ' ').trim();

  const doiMatch = clean.match(/10\.\d{4,9}\/\S+/);
  const yearMatch = clean.match(/\b(19|20)\d{2}\b/);
  const quotedMatch = clean.match(/[“"]([^”"]{8,})[”"]/);

  return {
    title: quotedMatch ? quotedMatch[1].trim() : '',
    authors: [],
    year: yearMatch ? yearMatch[0] : '',
    doi_or_url: doiMatch ? `https://doi.org/${doiMatch[0].replace(/[.,;]+$/, '')}` : '',
  };
}
